export interface BenchmarkProduct {
  id: string;
  name: string;
  manufacturer: string;
  product_type: string;
  ecolabels: {
    name: string;
    status: 'compliant' | 'partial' | 'not_compliant';
  }[];
  retailers: {
    name: string;
    status: 'compliant' | 'partial' | 'not_compliant';
  }[];
  states: {
    count: number;
    status: 'compliant' | 'partial' | 'not_compliant';
    details?: string;
  };
}

export const benchmarkProducts: BenchmarkProduct[] = [
  {
    id: 'bp1',
    name: 'EcoClean All-Purpose Cleaner',
    manufacturer: 'GreenTech Solutions',
    product_type: 'cleaning_product',
    ecolabels: [
      { name: 'Green Seal', status: 'compliant' },
      { name: 'Safer Choice', status: 'compliant' },
      { name: 'USDA BioPreferred', status: 'partial' }
    ],
    retailers: [
      { name: 'Costco', status: 'compliant' },
      { name: 'Amazon', status: 'compliant' },
      { name: 'Target', status: 'not_compliant' }
    ],
    states: {
      count: 45,
      status: 'compliant',
      details: 'Meets requirements in 45 states'
    }
  },
  {
    id: 'bp2',
    name: 'Natural Laundry Detergent',
    manufacturer: 'CleanCorp',
    product_type: 'laundry_product',
    ecolabels: [
      { name: 'Green Seal', status: 'compliant' },
      { name: 'Safer Choice', status: 'not_compliant' },
      { name: 'USDA BioPreferred', status: 'compliant' }
    ],
    retailers: [
      { name: 'Costco', status: 'partial' },
      { name: 'Amazon', status: 'compliant' },
      { name: 'Target', status: 'compliant' }
    ],
    states: {
      count: 32,
      status: 'partial',
      details: 'Meets requirements in 32 states, pending in 8 others'
    }
  },
  {
    id: 'bp3',
    name: 'Recycled Paper Towels',
    manufacturer: 'EcoPaper Inc',
    product_type: 'paper_product',
    ecolabels: [
      { name: 'Green Seal', status: 'compliant' },
      { name: 'Forest Stewardship Council', status: 'compliant' },
      { name: 'USDA BioPreferred', status: 'compliant' }
    ],
    retailers: [
      { name: 'Costco', status: 'compliant' },
      { name: 'Amazon', status: 'partial' },
      { name: 'Target', status: 'compliant' },
      { name: 'Walmart', status: 'compliant' }
    ],
    states: {
      count: 50,
      status: 'compliant',
      details: 'Meets requirements in all 50 states'
    }
  },
  {
    id: 'bp4',
    name: 'Bio-Based Floor Cleaner',
    manufacturer: 'NaturalCare',
    product_type: 'cleaning_product',
    ecolabels: [
      { name: 'Green Seal', status: 'partial' },
      { name: 'Safer Choice', status: 'compliant' },
      { name: 'USDA BioPreferred', status: 'compliant' }
    ],
    retailers: [
      { name: 'Costco', status: 'not_compliant' },
      { name: 'Amazon', status: 'compliant' },
      { name: 'Target', status: 'partial' }
    ],
    states: {
      count: 28,
      status: 'partial',
      details: 'Meets requirements in 28 states, under review in 12 others'
    }
  },
  {
    id: 'bp5',
    name: 'Sustainable Dish Soap',
    manufacturer: 'PureCorp',
    product_type: 'cleaning_product',
    ecolabels: [
      { name: 'Green Seal', status: 'not_compliant' },
      { name: 'Safer Choice', status: 'partial' },
      { name: 'USDA BioPreferred', status: 'not_compliant' }
    ],
    retailers: [
      { name: 'Costco', status: 'compliant' },
      { name: 'Amazon', status: 'compliant' },
      { name: 'Target', status: 'not_compliant' },
      { name: 'Walmart', status: 'partial' }
    ],
    states: {
      count: 15,
      status: 'partial',
      details: 'Meets requirements in 15 states, non-compliant in others'
    }
  },
  {
    id: 'bp6',
    name: 'Organic Fabric Softener',
    manufacturer: 'GreenWash Co',
    product_type: 'laundry_product',
    ecolabels: [
      { name: 'Green Seal', status: 'compliant' },
      { name: 'Safer Choice', status: 'compliant' },
      { name: 'USDA BioPreferred', status: 'partial' },
      { name: 'ECOLOGO', status: 'compliant' }
    ],
    retailers: [
      { name: 'Costco', status: 'compliant' },
      { name: 'Amazon', status: 'compliant' },
      { name: 'Target', status: 'compliant' }
    ],
    states: {
      count: 42,
      status: 'compliant',
      details: 'Meets requirements in 42 states'
    }
  }
];

export const ecolabelOptions = [
  'Green Seal',
  'Safer Choice',
  'USDA BioPreferred',
  'Forest Stewardship Council',
  'ECOLOGO'
];

export const retailerOptions = [
  'Costco',
  'Amazon',
  'Target',
  'Walmart'
];

export const stateRanges = [
  { label: 'All 50 states', min: 50, max: 50 },
  { label: '40-49 states', min: 40, max: 49 },
  { label: '25-39 states', min: 25, max: 39 },
  { label: '10-24 states', min: 10, max: 24 },
  { label: 'Less than 10 states', min: 0, max: 9 }
];

// Legacy benchmark data for existing components
export const kpiBenchmarkData = [
  {
    title: 'Renewable Energy Use',
    performance: 'excellent',
    yourValue: 85,
    unit: '%',
    industryAverage: 45,
    peerRangeMin: 30,
    peerRangeMax: 70
  },
  {
    title: 'Water Reduction',
    performance: 'good',
    yourValue: 12,
    unit: '%',
    industryAverage: 8,
    peerRangeMin: 3,
    peerRangeMax: 15
  },
  {
    title: 'Waste Diversion Rate',
    performance: 'average',
    yourValue: 72,
    unit: '%',
    industryAverage: 75,
    peerRangeMin: 60,
    peerRangeMax: 90
  }
];

export const chemicalSafetyBenchmarkData = [
  {
    title: 'Carcinogenicity',
    performance: 'excellent',
    yourValue: 0,
    unit: ' findings',
    industryAverage: 2.3,
    peerRangeMin: 0,
    peerRangeMax: 5
  },
  {
    title: 'Reproductive Toxicity',
    performance: 'good',
    yourValue: 1,
    unit: ' finding',
    industryAverage: 3.1,
    peerRangeMin: 0,
    peerRangeMax: 6
  },
  {
    title: 'Acute Toxicity',
    performance: 'average',
    yourValue: 8,
    unit: ' findings',
    industryAverage: 7.5,
    peerRangeMin: 4,
    peerRangeMax: 12
  }
];